"use client";

import Link from "next/link";
import { useParams } from "next/navigation";

import { Button } from "@/components/ui";

function StatusScreen({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-full w-full items-center justify-center bg-background p-8">
      <div className="max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <h1 className="mb-3 text-h2 text-foreground">{title}</h1>
        <p className="text-body text-foreground-secondary">{message}</p>
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}

/** The clinic name in the URL does not exist. */
export function ClinicNotFound() {
  return (
    <StatusScreen
      title="Clinic not found"
      message="We couldn't find a clinic at this booking link. Please check the link with your clinic and try again."
    />
  );
}

/** The clinic exists but the path under /self-schedule does not. */
export function PageNotFound() {
  const { tenant } = useParams<{ tenant: string }>();
  return (
    <StatusScreen
      title="Page not found"
      message="This page doesn't exist. You can still book an appointment from the clinic's booking page."
      action={
        <Button asChild variant="secondary">
          <Link href={`/${tenant}/self-schedule`}>Go to booking</Link>
        </Button>
      }
    />
  );
}

/** The clinic's booking data could not be loaded (network or server error). */
export function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <StatusScreen
      title="We couldn't load this page"
      message="Something went wrong while loading the booking page. Please try again in a moment."
      action={
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}
